import { useState } from "react";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { auth, isFirebaseConfigured } from "../firebase";
import useAuth from "../hooks/useAuth";
import styles from "./AdminPage.module.css";
import Icon from "../components/Icons";
import TestimonialsManager from "../components/admin/TestimonialsManager";
import BlogManager from "../components/admin/BlogManager";
import SiteSettingsManager from "../components/admin/SiteSettingsManager";

function NotConfigured() {
  return (
    <div className={styles.screen}>
      <div className={styles.card}>
        <h1 className={styles.title}>Admin panel not connected</h1>
        <p className={styles.text}>
          The Firebase configuration (REACT_APP_FIREBASE_*) is not set for
          this build. Copy <code>.env.example</code> to{" "}
          <code>.env.local</code>, fill in the project config, and rebuild.
        </p>
      </div>
    </div>
  );
}

function LoginScreen() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch {
      setError("Incorrect email or password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.screen}>
      <form className={styles.card} onSubmit={handleSubmit}>
        <span className={styles.lock}>
          <Icon name="pin" size={22} />
        </span>
        <h1 className={styles.title}>Admin sign in</h1>
        <p className={styles.text}>
          This area is only for managing the site. Patients should use the
          contact page.
        </p>

        <div className={styles.field}>
          <label htmlFor="admin-email">Email</label>
          <input
            id="admin-email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="admin-password">Password</label>
          <input
            id="admin-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <button type="submit" className={styles.submit} disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

const TABS = [
  { key: "testimonials", label: "Testimonials" },
  { key: "blog", label: "Blog" },
  { key: "settings", label: "Site details" },
];

function Dashboard({ user }) {
  const [tab, setTab] = useState("testimonials");

  return (
    <div className={styles.dashboard}>
      <header className={styles.dashHeader}>
        <div>
          <p className={styles.dashEyebrow}>Admin</p>
          <h1 className={styles.dashTitle}>Site management</h1>
        </div>
        <div className={styles.dashUser}>
          <span>{user.email}</span>
          <button
            type="button"
            className={styles.logout}
            onClick={() => signOut(auth)}
          >
            Sign out
          </button>
        </div>
      </header>

      <nav className={styles.tabs} aria-label="Admin sections">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`${styles.tab} ${tab === t.key ? styles.tabActive : ""}`}
            onClick={() => setTab(t.key)}
            aria-current={tab === t.key ? "page" : undefined}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className={styles.panel}>
        {tab === "testimonials" && <TestimonialsManager />}
        {tab === "blog" && <BlogManager />}
        {tab === "settings" && <SiteSettingsManager />}
      </div>
    </div>
  );
}

/** Route: /admin-aakash. Not linked from anywhere, noindex'd, and gated
    behind Firebase Auth — reachable only by someone who knows the URL
    and has valid admin credentials. */
export default function AdminPage() {
  const { user, loading } = useAuth();

  if (!isFirebaseConfigured) return <NotConfigured />;
  if (loading) {
    return (
      <div className={styles.screen}>
        <p className={styles.text}>Loading…</p>
      </div>
    );
  }
  if (!user) return <LoginScreen />;
  return <Dashboard user={user} />;
}
